import { AxiosError } from 'axios'
import moment from 'moment'
import { createStandardAction } from 'typesafe-actions'
import { AnyAction } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'

import { SSO_REALMS_URL } from '../../constants/urls/backend'
import { INewSSORealm, TSSORealm } from '../../types/ssoRealm'
import { IRequestResult } from '../types'
import { apiRoute } from '../../utils/routeUtils'
import { axiosWithAdal } from '../../utils/http'

const ssoRealmsSuccess = createStandardAction('@@ssoRealms/SUCCESS')<TSSORealm[]>()
const ssoRealmsError = createStandardAction('@@ssoRealms/ERROR')<string[]>()
const ssoRealmsRequest = createStandardAction('@@ssoRealms/REQUEST')()

const ssoRealmDeleteSuccess = createStandardAction('@@ssoRealms/DELETE_SUCCESS')<TSSORealm>()
const ssoRealmDeleteError = createStandardAction('@@ssoRealms/DELETE_ERROR')<string[]>()
const ssoRealmDeleteRequest = createStandardAction('@@ssoRealms/DELETE_REQUEST')()

const ssoRealmUpdateSuccess = createStandardAction('@@ssoRealms/UPDATE_SUCCESS')<TSSORealm>()
const ssoRealmUpdateError = createStandardAction('@@ssoRealms/UPDATE_ERROR')<string[]>()
const ssoRealmUpdateRequest = createStandardAction('@@ssoRealms/UPDATE_REQUEST')()

const ssoRealmCreateSuccess = createStandardAction('@@ssoRealms/CREATE_SUCCESS')<TSSORealm>()
const ssoRealmCreateError = createStandardAction('@@ssoRealms/CREATE_ERROR')<string[]>()
const ssoRealmCreateRequest = createStandardAction('@@ssoRealms/CREATE_REQUEST')()

interface IResponse {
  id: number
  realm: string
  idp_hint: string
  created: string
  updated: string
  installation: number
}

const fetchSsoRealms = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    dispatch(ssoRealmsRequest())
    try {
      const response = await axiosWithAdal.get<IResponse[]>(apiRoute(SSO_REALMS_URL), {
        headers: {
          'Content-Type': 'application/json',
        }
      })

      const payload: TSSORealm[] = response.data.map(x => {
        return {
          id: x.id,
          realm: x.realm,
          created: moment(x.created),
          updated: moment(x.updated),
          installation: x.installation,
          idpHint: x.idp_hint,
        }
      })

      dispatch(ssoRealmsSuccess(payload))
    } catch (e) {
      dispatch(ssoRealmsError([ e.message ]))
      return
    }
  }
}

const deleteSsoRealm = (ssoRealm: TSSORealm): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    dispatch(ssoRealmDeleteRequest())
    try {
      await axiosWithAdal.delete(`${apiRoute(SSO_REALMS_URL)}${ssoRealm.id}`, {
        headers: {
          'Content-Type': 'application/json',
        }
      })
      dispatch(ssoRealmDeleteSuccess(ssoRealm))

    } catch (e) {
      dispatch(ssoRealmDeleteError([ e.message ]))
    }
  }
}

const updateSsoRealm = (ssoRealm: TSSORealm): ThunkAction<Promise<IRequestResult>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<IRequestResult> => {

    interface IRequest {
      idp_hint: string
      installation: number
    }

    dispatch(ssoRealmUpdateRequest())
    try {
      const content: IRequest = {
        idp_hint: ssoRealm.idpHint,
        installation: ssoRealm.installation,
      }

      const response = await axiosWithAdal.put<IResponse>(
        `${apiRoute(SSO_REALMS_URL)}${ssoRealm.id}`, JSON.stringify(content), {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const payload: TSSORealm = {
        id: response.data.id,
        realm: response.data.realm,
        installation: response.data.installation,
        idpHint: response.data.idp_hint,
        created: moment(response.data.created),
        updated: moment(response.data.updated)
      }

      dispatch(ssoRealmUpdateSuccess(payload))

      return {
        status: response.status,
        message: response.statusText,
      }
    } catch (e) {
      dispatch(ssoRealmUpdateError([ e.message ]))
      const error = e as AxiosError
      return {
        status: error.code ? parseInt(error.code, 10) : 500,
        message: error.message
      }
    }
  }
}

const createSsoRealm = (
  ssoRealm: INewSSORealm
): ThunkAction<Promise<IRequestResult>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<IRequestResult> => {
    dispatch(ssoRealmCreateRequest())
    try {

      const response = await axiosWithAdal.post<IResponse>(
        apiRoute(SSO_REALMS_URL), JSON.stringify({
          idp_hint: ssoRealm.idpHint,
          realm: ssoRealm.realm,
          installation: ssoRealm.installation,
        }), {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      dispatch(ssoRealmCreateSuccess({
        id: response.data.id,
        idpHint: response.data.idp_hint,
        created: moment(response.data.created),
        updated: moment(response.data.updated),
        installation: response.data.installation,
        realm: response.data.realm,
      }))

      return {
        status: response.status,
        message: response.statusText,
      }
    } catch (e) {
      dispatch(ssoRealmCreateError([ e.message ]))
      const error = e as AxiosError
      return {
        status: error.code ? parseInt(error.code, 10) : 500,
        message: error.message
      }
    }
  }
}

export {
  fetchSsoRealms,
  createSsoRealm,
  updateSsoRealm,
  deleteSsoRealm,
  ssoRealmsSuccess,
  ssoRealmsRequest,
  ssoRealmsError,
  ssoRealmDeleteSuccess,
  ssoRealmDeleteError,
  ssoRealmDeleteRequest,
  ssoRealmCreateSuccess,
  ssoRealmCreateError,
  ssoRealmCreateRequest,
  ssoRealmUpdateSuccess,
  ssoRealmUpdateError,
  ssoRealmUpdateRequest,
}
